import type { OverridableBookingCode } from "@teamna/shared";

/**
 * Friendly English copy for the API's stable RFC 7807 error codes (TRD section 4).
 *
 * Mirrors `apps/mobile/src/api/copy.ts`. The rule is the same on both surfaces: the member
 * should always know what happened and what to do next, in one short sentence, with no jargon from
 * the domain model. Screens map `error.code` through here; nothing ever renders a raw message.
 */

export type ErrorCopy = {
  title: string;
  message: string;
  /** Label for the primary recovery action, when one makes sense. */
  action?: string;
};

const ERROR_COPY: Record<string, ErrorCopy> = {
  /** Subscription billing: the business did not pay Teamna, so Desk is read-only for the owner. */
  BILLING_READ_ONLY: {
    title: "Desk is read-only",
    message: "The Teamna plan of this business is not paid. You can see all your data. Add a card on the Billing page to make changes again.",
    action: "Open billing",
  },
  POLICY_CUTOFF_PASSED: {
    title: "Past the cancellation window",
    message:
      "The free cancellation window for this session has closed. Cancelling now follows the business policy shown before you confirm.",
  },
  SESSION_FULL: {
    title: "This session is full",
    message: "Someone took the last spot. Pick another time and we will book that one instead.",
    action: "Pick another time",
  },
  INSUFFICIENT_CREDITS: {
    title: "No sessions left",
    message: "You have no sessions left on an active package. Buy a package to keep booking.",
    action: "View packages",
  },
  RESCHEDULE_NOT_ALLOWED: {
    title: "This slot is set by your business",
    message:
      "Weekly slots are arranged by the business, so they cannot be moved from the app. Contact your business to change the day or time. You can still cancel a single week.",
  },
  RESCHEDULE_LIMIT_REACHED: {
    title: "Reschedule limit reached",
    message:
      "You have used all the reschedules your business allows for this booking. Ask the business if you need another one.",
  },
  /**
   * The invite codes.
   *
   * `INVITE_NOT_FOUND` is what a mistyped code actually raises. The mobile app maps a code called
   * `INVITE_INVALID`, which is not in the shared registry and which the API therefore never sends,
   * so on that surface a typo currently falls through to the generic message. Both names are kept
   * here: the real one so the web app is right today, the other so the two apps do not disagree if
   * mobile is corrected.
   */
  INVITE_NOT_FOUND: {
    title: "Invite code not recognised",
    message: "Check the code with your business, or ask them to send the invite link again.",
    action: "Try another code",
  },
  INVITE_INVALID: {
    title: "Invite code not recognised",
    message: "Check the code with your business, or ask them to send the invite link again.",
    action: "Try another code",
  },
  INVITE_EXPIRED: {
    title: "Invite code expired",
    message: "This invite is no longer valid. Ask your business for a fresh link.",
  },
  INVITE_EXHAUSTED: {
    title: "Invite code already used",
    message: "This code has been used as many times as your business allowed. Ask them for a new one.",
  },
  ALREADY_A_MEMBER: {
    title: "You are already in",
    message: "You joined this business before, so there is nothing to do. Your schedule is up to date.",
  },
  CONVERSATION_NOT_FOUND: {
    title: "Conversation not found",
    message: "That conversation does not exist, or you are not part of it.",
  },
  ANNOUNCEMENT_READ_ONLY: {
    title: "Announcements are one way",
    message: "Only the business can post here. Reply in your private chat with the business instead.",
  },
  MESSAGE_RATE_LIMITED: {
    title: "Slow down",
    message: "Too many messages in a short time. Wait a few minutes and try again.",
  },
  MEMBER_HAS_LEFT: {
    title: "This member has left",
    message: "They left the business, so you cannot book for them. Invite them again with the join code.",
  },
  ATTENDANCE_TOO_EARLY: {
    title: "Too early to mark",
    message: "You can mark who came from the day of the session.",
  },
  ATTENDANCE_CHANGED: {
    title: "Attendance changed",
    message: "Someone changed this session on another device. Check the list again, then save.",
  },
  COVER_ALREADY_ASKED: {
    title: "Cover already asked",
    message: "There is an open cover request for this session. Wait for an answer, or cancel it first.",
  },
  COVER_NOBODY_TO_ASK: {
    title: "Nobody to ask",
    message: "Nobody else is on the team, so nobody can take this session. Ask the owner.",
  },
  COVER_TAKEN: {
    title: "Someone took it",
    message: "Another person took this cover first.",
  },
  COVER_CLOSED: {
    title: "Cover request closed",
    message: "This cover request is closed: it was taken back, it expired, or the session changed.",
  },
  COVER_NOT_OFFERED: {
    title: "Not offered to you",
    message: "This cover request is not for you. Only the people who got the offer can take it.",
  },
  NOT_YOUR_SESSION: {
    title: "Not your session",
    message: "You can mark who came only on the sessions you run. Ask the owner to put you on this session.",
  },
  MEMBERSHIP_NOT_ACTIVE: {
    title: "Your membership is not active",
    message:
      "Your business has not approved this membership yet, or has paused it. Booking starts when the membership is active. Contact your business if you need help.",
  },
  NOT_A_MEMBER: {
    title: "Not a member yet",
    message: "You are not part of this business yet. Join with the invite code your business sent you.",
    action: "Join with a code",
  },
  OTHER_GROUP_SESSION: {
    title: "Not their group",
    message: "This session is for another group. Pick a session of their own group, or ask the business.",
  },
  BOOKINGS_REFUSED: {
    title: "Nothing was booked",
    message: "Some sessions could not be booked. They are marked in your list. Change them, then confirm again.",
  },
  SESSION_CHANGED: {
    title: "This session moved",
    message: "The business moved this session after you picked it. Look at the new time, then pick it again.",
  },
  WAITLIST_CLOSED: {
    title: "Waiting list closed",
    message: "The waiting list of this session is closed. It closes some hours before the session starts.",
  },
  WAITLIST_OFFER_EXPIRED: {
    title: "The offer ended",
    message: "The time to accept this place ended. The place went to the next person on the waiting list.",
  },
  SESSION_NOT_FULL: {
    title: "A place is free",
    message: "This session has a free place now. Pick it to book it.",
  },
  WAITLIST_ENTRY_NOT_FOUND: {
    title: "Not on the waiting list",
    message: "This place on the waiting list is gone. It may have been booked a moment ago: look at your schedule.",
  },
  ALREADY_BOOKED: {
    title: "Already booked",
    message: "This session is already on your schedule, so there is nothing left to book.",
  },
  SESSION_CANCELLED: {
    title: "This session was cancelled",
    message: "Your business called this one off. Pick another time and we will book that one instead.",
    action: "Pick another time",
  },
  SESSION_STARTED: {
    title: "This session has started",
    message: "Booking closes once a class is under way. Pick a later time instead.",
    action: "Pick another time",
  },
  BOOK_TOO_SOON: {
    title: "Too close to the start",
    message: "Your business stops taking bookings shortly before a class begins. Pick a later time.",
    action: "Pick another time",
  },
  BOOK_TOO_FAR_AHEAD: {
    title: "Not open for booking yet",
    message: "This session opens for booking later. Come back closer to the date.",
  },
  GROUP_MISMATCH: {
    title: "Your package does not cover this group",
    message: "The sessions you have are for a different group. Buy a package that includes this one.",
    action: "View packages",
  },
  PACKAGE_EXPIRED: {
    title: "Your package ends before this session",
    message: "Your package cannot pay for a session after its end date. Buy a new package to book it.",
    action: "View packages",
  },
  PARTICIPANT_HAS_BOOKINGS: {
    title: "There are still sessions booked",
    message: "Cancel the upcoming sessions for this person first, then you can remove the profile.",
  },
  MEMBERSHIP_CAP_REACHED: {
    title: "No sessions left this month",
    message: "You used every session of your membership this month. You can book again when the next month starts.",
  },
  MEMBERSHIP_MONTH_UNPAID: {
    title: "This month is not paid",
    message: "The month of this session is not paid yet. Pay it in Payments, then book the session.",
    action: "Go to Payments",
  },
  MEMBERSHIP_RUNNING: {
    title: "Membership already running",
    message: "There is a running membership here already. Cancel it first, or wait until it ends.",
  },
  MEMBERSHIP_NO_FREEZE: {
    title: "A membership cannot be paused",
    message: "A monthly membership cannot be paused. Cancel it at the end of the month instead.",
  },
  NEW_CLIENTS_ONLY: {
    title: "This offer is for a first purchase",
    message: "Only a person who has not bought or come here before can buy this package. Pick another package.",
    action: "View packages",
  },
  PAYMENT_REQUEST_NOT_PAYABLE: {
    title: "Nothing left to pay",
    message: "This one has already been settled or cancelled by your business.",
  },
  PAYMENT_PROVIDER_ERROR: {
    title: "The payment page did not open",
    message: "The card provider did not answer. Nothing was charged. Try again in a moment.",
    action: "Try again",
  },
  VALIDATION_FAILED: {
    title: "Check the details",
    message: "Something in that form is not quite right. Look it over and try again.",
  },
  FORBIDDEN: {
    title: "Not allowed",
    message: "Your role does not include this action. Ask the business owner if you need access.",
  },
  OTP_INVALID: {
    title: "That code is not right",
    message: "Check the code we sent you, or ask for a new one.",
  },
  OTP_TOO_MANY_ATTEMPTS: {
    title: "Too many tries",
    message: "That code is no longer usable. Ask for a new one.",
    action: "Send a new code",
  },
  WHATSAPP_UNAVAILABLE: {
    title: "WhatsApp did not answer",
    message: "Teamna could not reach WhatsApp. Try again in a moment.",
    action: "Try again",
  },
  OTP_SEND_FAILED: {
    title: "We could not send the code",
    message: "Check the number or the email address, then try again.",
    action: "Try again",
  },
  IDENTIFIER_IN_USE: {
    title: "Already on another account",
    message: "This is already on another Teamna account. Sign out and sign in with it to use it.",
    action: "Sign out",
  },
  LAST_SIGN_IN_METHOD: {
    title: "Last way to sign in",
    message: "Add and verify another way to sign in first.",
  },
  INVALID_CREDENTIALS: {
    title: "That did not match",
    message: "The username and password do not match a test account. Check both and try again.",
  },
  /** Raised by the client when the session token is gone or rejected. */
  SESSION_EXPIRED: {
    title: "Signed out",
    message: "Your session ended. Sign in again to pick up where you left off.",
    action: "Sign in",
  },
  /** Raised by the client when a response does not match the shared zod contract. */
  RESPONSE_SHAPE: {
    title: "Something changed",
    message:
      "The server answered in a shape this version of the app does not understand. Reload the page to pick up the latest version.",
    action: "Reload",
  },
};

const NETWORK_COPY: ErrorCopy = {
  title: "No connection",
  message: "We could not reach the server. Check your connection and try again.",
  action: "Try again",
};

const FALLBACK_COPY: ErrorCopy = {
  title: "Something went wrong",
  message: "That did not work. Please try again in a moment.",
  action: "Try again",
};

/** Maps a known error code to copy, falling back to a safe generic message. */
/** Codes whose server `detail` says more than the static copy, so a screen shows the detail. */
const DETAIL_FIRST = new Set(["VALIDATION_FAILED", "BOOK_TOO_FAR_AHEAD", "PACKAGE_EXPIRED"]);

export function copyForErrorCode(code: string | undefined): ErrorCopy {
  if (!code) return FALLBACK_COPY;
  if (code === "NETWORK_ERROR" || code === "TIMEOUT") return NETWORK_COPY;
  return ERROR_COPY[code] ?? FALLBACK_COPY;
}

/**
 * Copy for anything thrown by the client or a hook. Prefers the mapped copy for a known code, then
 * the server's own problem+json detail, then the fallback.
 */
export function copyForError(error: unknown): ErrorCopy {
  if (error && typeof error === "object" && "code" in error) {
    const apiError = error as { code?: string; title?: string; detail?: string };
    const mapped = copyForErrorCode(apiError.code);
    // A validation problem is only useful when it names the field and what to do, and the API
    // writes that sentence ("Check "from". Use a date and time, for example ..."). The static copy
    // can only say "something is wrong".
    // The same for a booking refused for its date: the API names the day the session opens or the
    // day the package ends (B3), which the static copy cannot.
    if (apiError.code && DETAIL_FIRST.has(apiError.code) && apiError.detail) {
      return { title: mapped.title, message: apiError.detail, ...(mapped.action ? { action: mapped.action } : {}) };
    }
    if (mapped !== FALLBACK_COPY) return mapped;
    if (apiError.detail) {
      return {
        title: apiError.title ?? FALLBACK_COPY.title,
        message: apiError.detail,
        action: "Try again",
      };
    }
  }
  return FALLBACK_COPY;
}

/**
 * The words for a refused add on the owner's session panel (#ai-feedback 2026-10-26, card #6). The
 * shared copy speaks to the member ("Your package ..."); here the owner reads about the client, by
 * name. `overridable`: a refusal about packages or payment (`overridableBookingCodes`), so the panel
 * offers Add anyway and Tell the client. Null for a refusal with no owner words: the caller shows
 * the shared copy.
 */
export function ownerAddCopy(error: unknown, who: { name: string; groupName: string }): (ErrorCopy & { code: OverridableBookingCode | null; unpaid?: boolean }) | null {
  const code = error && typeof error === "object" && "code" in error ? (error as { code?: string }).code : undefined;
  const meta = error && typeof error === "object" && "meta" in error ? (error as { meta?: { reason?: unknown } }).meta : undefined;
  const { name, groupName } = who;
  const refused = (overridable: OverridableBookingCode, title: string, message: string) => ({ title, message, code: overridable });
  switch (code) {
    case "GROUP_MISMATCH":
      return refused(code, `${name} has no package for ${groupName}`, "Their package is for another class. It cannot pay for this session.");
    case "INSUFFICIENT_CREDITS":
      // A package with sessions left that is not paid yet pays for no booking until it is paid.
      if (meta?.reason === "unpaid") {
        return { ...refused(code, `${name}'s package is not paid yet`, "A package pays for a session only after it is paid. Record the payment, or add them anyway."), unpaid: true };
      }
      return refused(code, `${name} has no sessions left`, `No package of theirs has a session left for ${groupName}.`);
    case "PACKAGE_EXPIRED":
      return refused(code, `${name}'s package ends before this session`, "A package cannot pay for a session after its end date.");
    case "PACKAGE_PAUSED":
      return refused(code, `${name}'s package is paused on this day`, "A paused package pays for no session on a paused day.");
    case "MEMBERSHIP_CAP_REACHED":
      return refused(code, `${name} used every session of the membership this month`, "The membership pays for no more sessions this month.");
    case "MEMBERSHIP_MONTH_UNPAID":
      return refused(code, `The month of this session is not paid for ${name}`, "The membership pays for a session only in a paid month.");
    case "ALREADY_BOOKED":
      return { title: `${name} is already on this session`, message: "There is nothing to add.", code: null };
    case "SESSION_FULL":
      return { title: "This session is full", message: "Take someone off first, or add them to another session.", code: null };
    case "SESSION_STARTED":
      return { title: "This session has started", message: "You add a client before the start. For someone who came, take attendance.", code: null };
    case "SESSION_CANCELLED":
      return { title: "This session was cancelled", message: "Add them to another session.", code: null };
    case "MEMBER_HAS_LEFT":
      return { title: `${name} left`, message: "Invite them again with the join code first.", code: null };
    default:
      return null;
  }
}
